import { lazy, Suspense, useEffect, useLayoutEffect } from "react";
import { motion, MotionConfig, useReducedMotion } from "motion/react";
import { pageTransition } from "./lib/motion";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router";
import { Nav } from "./components/layout/Navbar";
import { Footer } from "./components/layout/Footer";
import { CookieConsentProvider } from "./components/shared/cookie-consent/CookieConsentContext";
import { CookieConsentBanner } from "./components/shared/cookie-consent/CookieConsentBanner";
import { CookiePreferenceCenter } from "./components/shared/cookie-consent/CookiePreferenceCenter";
import { useHasConsent } from "./components/shared/cookie-consent/CookieConsentContext";
import { useLenis } from "./lib/lenis.tsx";
import { posthog, syncPostHogConsent } from "./lib/posthog";
import { Sentry, syncSentryConsent } from "./lib/sentry";
import { HomePage } from "./pages/HomePage";
import { IvizPage } from "./pages/IvizPage";
import { ForesightPage } from "./pages/ForesightPage";

const EvidencePage = lazy(() =>
  import("./pages/EvidencePage").then((m) => ({ default: m.EvidencePage }))
);
const PricingPage = lazy(() =>
  import("./pages/PricingPage").then((m) => ({ default: m.PricingPage }))
);
const ContactPage = lazy(() =>
  import("./pages/ContactPage").then((m) => ({ default: m.ContactPage }))
);
const PrivacyPolicyPage = lazy(() =>
  import("./pages/legal/PrivacyPolicyPage").then((m) => ({ default: m.PrivacyPolicyPage }))
);
const CookiePolicyPage = lazy(() =>
  import("./pages/legal/CookiePolicyPage").then((m) => ({ default: m.CookiePolicyPage }))
);
const TermsAndConditionsPage = lazy(() =>
  import("./pages/legal/TermsAndConditionsPage").then((m) => ({ default: m.TermsAndConditionsPage }))
);
const CancellationPolicyPage = lazy(() =>
  import("./pages/legal/CancellationPolicyPage").then((m) => ({ default: m.CancellationPolicyPage }))
);
const PromoPage = lazy(() =>
  import("./pages/PromoPage").then((m) => ({ default: m.PromoPage }))
);

function prefetchSecondaryPages() {
  import("./pages/EvidencePage");
  import("./pages/ContactPage");
}

function PageLoading() {
  return (
    <div role="status" className="min-h-[70vh] pt-32 px-6 text-center text-sm text-[#5C5C66] dark:text-[#9A9A9A]">
      Loading page?
    </div>
  );
}

function LegacyContactRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: "/contact/foresight", search, hash }} replace />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  const lenis = useLenis();
  useLayoutEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, lenis]);
  return null;
}

function AnalyticsPageview() {
  const location = useLocation();
  useEffect(() => {
    posthog.capture("$pageview");
  }, [location.pathname]);
  return null;
}

function AnalyticsConsentSync() {
  const analyticsConsent = useHasConsent("analytics");
  useEffect(() => {
    syncPostHogConsent(analyticsConsent);
    syncSentryConsent(analyticsConsent);
  }, [analyticsConsent]);
  return null;
}

function AppShell() {
  const { pathname } = useLocation();
  const reduced = useReducedMotion();
  // Prefetch secondary chunks only after window.load — fonts, images, and JS must
  // finish first so prefetch doesn't compete with critical resources on slow connections
  useEffect(() => {
    const kick = () => {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(prefetchSecondaryPages, { timeout: 5000 });
      } else {
        setTimeout(prefetchSecondaryPages, 2000);
      }
    };
    if (document.readyState === "complete") {
      kick();
    } else {
      window.addEventListener("load", kick, { once: true });
    }
  }, []);

  return (
    <CookieConsentProvider>
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505]">
        <Nav />
        <ScrollToTop />
        {/* Consent sync first: sibling effects run in order, so PostHog is initialized and
            opted in before the landing page's $pageview is captured. */}
        <AnalyticsConsentSync />
        <AnalyticsPageview />
        {/* Opacity-only transitions preserve fixed overlays and GSAP pin geometry. */}
        <motion.main
          key={pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={reduced ? { duration: 0.12 } : pageTransition}
        >
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/iviz" element={<IvizPage />} />
          <Route path="/foresight" element={<ForesightPage />} />
          <Route
            path="/evidence"
            element={
              <Suspense fallback={<PageLoading />}>
                <EvidencePage />
              </Suspense>
            }
          />
          <Route
            path="/pricing"
            element={
              <Suspense fallback={<PageLoading />}>
                <PricingPage />
              </Suspense>
            }
          />
          <Route path="/contact" element={<LegacyContactRedirect />} />
          <Route path="/contact/foresight" element={
            <Suspense fallback={<PageLoading />}><ContactPage key="foresight" product="foresight" /></Suspense>
          } />
          <Route path="/contact/iviz" element={
            <Suspense fallback={<PageLoading />}><ContactPage key="iviz" product="iviz" /></Suspense>
          } />
          <Route
            path="/privacy-policy"
            element={
              <Suspense fallback={<PageLoading />}>
                <PrivacyPolicyPage />
              </Suspense>
            }
          />
          <Route
            path="/cookie-policy"
            element={
              <Suspense fallback={<PageLoading />}>
                <CookiePolicyPage />
              </Suspense>
            }
          />
          <Route
            path="/terms-and-conditions"
            element={
              <Suspense fallback={<PageLoading />}>
                <TermsAndConditionsPage />
              </Suspense>
            }
          />
          <Route
            path="/cancellation-policy"
            element={
              <Suspense fallback={<PageLoading />}>
                <CancellationPolicyPage />
              </Suspense>
            }
          />
          <Route
            path="/promo"
            element={
              <Suspense fallback={<PageLoading />}>
                <PromoPage />
              </Suspense>
            }
          />
        </Routes>
        </motion.main>
        <Footer />
        <CookieConsentBanner />
        <CookiePreferenceCenter />
      </div>
    </CookieConsentProvider>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
      <MotionConfig reducedMotion="user" transition={pageTransition}>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </MotionConfig>
    </Sentry.ErrorBoundary>
  );
}
